import { useSelector } from "react-redux";
import {
  Warning,
  ShieldCheck,
  Clock,
  Prohibit,
  CalendarX,
  Info,
} from "@phosphor-icons/react";
import { selectTherapistId } from "../../../../store/slices/authSlice";
import {
  absenceLabel,
  canSeeAbsenceDetails,
  formatAbsenceRange,
} from "../../../../utils/absenceUtils";
import LoaderLoop from "../../../shared/loader/LoaderLoop";
import "./TherapistConflictPanel.css";

const hhmm = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toTimeString().slice(0, 5);
};

const focusTimeField = () => {
  if (typeof document === "undefined") return;
  const el =
    document.querySelector('.apt-wiz-step [name="startTime"]') ||
    document.querySelector('[name="startTime"]');
  if (el) {
    el.scrollIntoView?.({ block: "center", behavior: "smooth" });
    el.focus?.();
  }
};

/**
 * Therapist double-booking + absence panel (Schedule step). Mirrors
 * RoomBlockedPanel: reason line, suggestions, policy-gated one-click waiver.
 *
 * Policy (clinic.multiClinicSettings, absent ⇒ warn):
 *   preventTherapistDoubleBooking / blockBookingOnAbsence
 *   false ⇒ anyone may "קבע/י בכל זאת"; true ⇒ admins only.
 * Every waiver is stamped on the appointment and written to the activity
 * log (override_therapist_conflict / override_absence).
 *
 * `informational` (prop, or form.therapistGateInformational): an edit whose
 * slot was not touched — the panel reports the overlap/absence without the
 * waiver buttons; changing the slot re-arms the gate.
 */
function TherapistConflictPanel({ form, informational }) {
  const currentTherapistId = useSelector(selectTherapistId);
  const {
    therapistId,
    therapistName,
    isAdmin,
    overlappingAppointment,
    therapistConflicts = [],
    therapistCheckPending,
    therapistCheckFailed,
    absenceCheckFailed,
    overlapConfirmed,
    setOverlapConfirmed,
    canOverrideTherapistConflict,
    therapistAbsence,
    absenceConfirmed,
    setAbsenceConfirmed,
    canOverrideAbsence,
    therapistGateInformational,
  } = form;
  const infoOnly =
    informational !== undefined ? !!informational : therapistGateInformational === true;

  const isSelf = !therapistId || therapistId === currentTherapistId;
  const who = isSelf ? "לך" : `ל${therapistName || "מטפל/ת"}`;

  if (therapistCheckPending && !overlappingAppointment && !therapistAbsence) {
    return (
      <div className="tcp-panel tcp-pending" aria-live="polite">
        <LoaderLoop size="xs" />
        <span>בודק חפיפות ביומן…</span>
      </div>
    );
  }

  // Failed checks are reported, never gated — a silent "free" would be a lie.
  const failureNotes = [];
  if (therapistCheckFailed && !overlappingAppointment) {
    failureNotes.push(
      isSelf
        ? "לא ניתן לבדוק חפיפות כרגע"
        : `לא ניתן לבדוק חפיפות ביומן של ${therapistName || "המטפל/ת"}.`
    );
  }
  if (absenceCheckFailed && !therapistAbsence) {
    failureNotes.push("לא ניתן לבדוק היעדרויות כרגע");
  }
  const renderFailureNotes = () =>
    failureNotes.map((note) => (
      <div key={note} className="tcp-panel tcp-muted">
        <Warning size={14} weight="fill" />
        <span>{note}</span>
      </div>
    ));

  if (!overlappingAppointment && !therapistAbsence) {
    if (failureNotes.length === 0) return null;
    return <div className="tcp-stack">{renderFailureNotes()}</div>;
  }

  // Informational edit: no waiver buttons; a single muted note explains why.
  const infoNote = infoOnly ? (
    <div className="tcp-panel tcp-muted" role="note">
      <Info size={14} weight="fill" />
      <span>התור קיים כבר במועד חופף; שינוי המועד יפעיל בדיקה מחדש</span>
    </div>
  ) : null;

  // ----- conflict copy -----
  const canSeeConflictNames = (c) =>
    isAdmin || c.therapistId === currentTherapistId;
  const describeConflict = (c) => {
    const range = `${hhmm(c.startTime)}–${hhmm(c.endTime)}`;
    if (c.isBlockedTime) return `זמן חסום ${range}`;
    const name = canSeeConflictNames(c) && c.patientFirstName ? c.patientFirstName : "תור קיים";
    return `${name} ${range}`;
  };
  const extraConflicts = Math.max(0, therapistConflicts.length - 3);

  // ----- absence copy -----
  const absenceCanSee = therapistAbsence
    ? canSeeAbsenceDetails(therapistAbsence, { viewerId: currentTherapistId, isAdmin })
    : false;

  return (
    <div className="tcp-stack">
      {overlappingAppointment && (
        <div
          className={`tcp-panel ${!infoOnly && overlapConfirmed && canOverrideTherapistConflict ? "overridden" : ""}`}
          role="status"
        >
          <div className="tcp-reason">
            {overlappingAppointment.isBlockedTime ? (
              <Clock size={16} weight="fill" />
            ) : (
              <Warning size={16} weight="fill" />
            )}
            <span>
              {overlappingAppointment.isBlockedTime
                ? `המועד חופף לזמן חסום ביומן ${isSelf ? "שלך" : `של ${therapistName || "המטפל/ת"}`}: ${describeConflict(overlappingAppointment)}`
                : `${who} כבר יש תור בשעה זו: ${describeConflict(overlappingAppointment)}`}
            </span>
          </div>
          {therapistConflicts.length > 1 && (
            <ul className="tcp-list">
              {therapistConflicts.slice(1, 3).map((c) => (
                <li key={c.id}>{describeConflict(c)}</li>
              ))}
              {extraConflicts > 0 && <li>ועוד {extraConflicts} חפיפות…</li>}
            </ul>
          )}

          {infoOnly ? null : overlapConfirmed && canOverrideTherapistConflict ? (
            <div className="tcp-override-note">
              <ShieldCheck size={16} weight="fill" />
              <span>אושר — התור ייקבע למרות החפיפה. הפעולה נרשמת ביומן הפעילות.</span>
            </div>
          ) : (
            <div className="tcp-actions">
              <button type="button" className="tcp-btn-secondary" onClick={focusTimeField}>
                בחירת שעה אחרת
              </button>
              {canOverrideTherapistConflict ? (
                <button
                  type="button"
                  className="apt-wiz-gate-btn"
                  onClick={() => setOverlapConfirmed(true)}
                >
                  קבע/י בכל זאת
                </button>
              ) : (
                <span className="tcp-blocked">
                  <Prohibit size={14} weight="bold" />
                  מדיניות הקליניקה חוסמת כפל תורים למטפל/ת. רק מנהל/ת יכול/ה לעקוף.
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {therapistAbsence && (
        <div
          className={`tcp-panel tcp-absence ${!infoOnly && absenceConfirmed && canOverrideAbsence ? "overridden" : ""}`}
          role="status"
        >
          <div className="tcp-reason">
            <CalendarX size={16} weight="fill" />
            <span>
              {isSelf ? "את/ה" : therapistName || "המטפל/ת"} לא זמין/ה בתאריך זה
              {absenceCanSee
                ? ` (${absenceLabel(therapistAbsence, { canSeeDetails: true })}${
                    therapistAbsence.allDay === false && therapistAbsence.startTime
                      ? ` ${therapistAbsence.startTime}–${therapistAbsence.endTime}`
                      : ` · ${formatAbsenceRange(therapistAbsence)}`
                  })`
                : ""}
            </span>
          </div>

          {infoOnly ? null : absenceConfirmed && canOverrideAbsence ? (
            <div className="tcp-override-note">
              <ShieldCheck size={16} weight="fill" />
              <span>אושר — התור ייקבע בזמן ההיעדרות. הפעולה נרשמת ביומן הפעילות.</span>
            </div>
          ) : (
            <div className="tcp-actions">
              <button type="button" className="tcp-btn-secondary" onClick={focusTimeField}>
                בחירת מועד אחר
              </button>
              {canOverrideAbsence ? (
                <button
                  type="button"
                  className="apt-wiz-gate-btn"
                  onClick={() => setAbsenceConfirmed(true)}
                >
                  קבע/י בכל זאת
                </button>
              ) : (
                <span className="tcp-blocked">
                  <Prohibit size={14} weight="bold" />
                  מדיניות הקליניקה חוסמת קביעת תורים בזמן היעדרות. רק מנהל/ת יכול/ה לעקוף.
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {infoNote}
      {renderFailureNotes()}
    </div>
  );
}

export default TherapistConflictPanel;
